/*
  Label Lique cart drawer: "frequently bought together" slider (ported from the Stylishr cart).
  For each product in the cart it loads the complementary products set in Search & Discovery
  (intent=complementary). When there are too few, it tops up with related products (intent=related).
  Results are cached per product so the slider doesn't reload when the drawer re-renders after a
  quantity change.
*/
(() => {
  if (customElements.get('ll-cart-recs')) return;

  const MAX_SOURCE_PRODUCTS = 4;
  const MAX_ITEMS = 10;
  const MIN_ITEMS = 4;
  const cache = new Map();
  let lastRender = null;
  let lastScroll = 0;

  const fetchRecs = (url, productId, intent) => {
    const key = `${intent}:${productId}`;
    if (!cache.has(key)) {
      const request = fetch(
        `${url}?product_id=${productId}&limit=${MAX_ITEMS}&intent=${intent}&section_id=ll-cart-recommendations`
      )
        .then((response) => {
          if (response.ok) return response.text();
          cache.delete(key);
          return '';
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return [...doc.querySelectorAll('.ll-cart-rec')].map((el) => ({
            id: el.dataset.productId,
            html: el.outerHTML,
          }));
        })
        .catch(() => {
          cache.delete(key);
          return [];
        });
      cache.set(key, request);
    }
    return cache.get(key);
  };

  // Take turns from each list so every product in the cart contributes.
  const merge = (lists, exclude) => {
    const seen = new Set(exclude);
    const result = [];
    const longest = Math.max(0, ...lists.map((list) => list.length));
    for (let i = 0; i < longest && result.length < MAX_ITEMS; i++) {
      for (const list of lists) {
        const item = list[i];
        if (item && !seen.has(item.id)) {
          seen.add(item.id);
          result.push(item);
          if (result.length >= MAX_ITEMS) break;
        }
      }
    }
    return result;
  };

  class LlCartRecs extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keyup', this.onKeyUp.bind(this));
    }

    connectedCallback() {
      this.track = this.querySelector('.ll-cart-recs__track');
      this.headingEl = this.querySelector('.ll-cart-recs__heading');
      this.ids = (this.dataset.productIds || '').split(',').filter(Boolean);
      this.key = this.ids.join(',');
      this.track.addEventListener(
        'scroll',
        () => {
          lastScroll = this.track.scrollLeft;
          this.updateArrows();
        },
        { passive: true }
      );

      if (lastRender) {
        const exclude = new Set(this.ids);
        const items = lastRender.items.filter((item) => !exclude.has(item.id));
        this.show(lastRender.heading, items, lastRender.key === this.key);
      }

      // Only fetch once the drawer is opened, not on every page view with a filled cart.
      const drawer = this.closest('cart-drawer');
      if (!drawer || drawer.classList.contains('active')) {
        this.load();
      } else {
        this.drawerObserver = new MutationObserver(() => {
          if (!drawer.classList.contains('active')) return;
          this.drawerObserver.disconnect();
          this.load();
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      this.drawerObserver?.disconnect();
    }

    async load() {
      const url = this.dataset.url;
      if (!url || !this.ids.length) return;
      const sources = this.ids.slice(0, MAX_SOURCE_PRODUCTS);

      let heading = this.dataset.heading;
      let items = merge(await Promise.all(sources.map((id) => fetchRecs(url, id, 'complementary'))), this.ids);

      // Too few complementary products for a slider: top up with related products.
      if (items.length < MIN_ITEMS && this.dataset.fallback === 'true') {
        const related = merge(
          await Promise.all(sources.slice(0, 2).map((id) => fetchRecs(url, id, 'related'))),
          [...this.ids, ...items.map((item) => item.id)]
        );
        if (!items.length) heading = this.dataset.fallbackHeading;
        items = [...items, ...related].slice(0, MAX_ITEMS);
      }

      if (!this.isConnected) return;
      const unchanged =
        lastRender &&
        lastRender.key === this.key &&
        lastRender.items.map((i) => i.id).join() === items.map((i) => i.id).join();
      lastRender = { key: this.key, heading, items };
      if (!unchanged || this.hidden) this.show(heading, items, lastRender.key === this.key);
    }

    show(heading, items, keepScroll) {
      if (!items.length) {
        this.hidden = true;
        return;
      }
      this.headingEl.textContent = heading;
      this.track.innerHTML = items.map((item) => item.html).join('');
      this.hidden = false;
      this.track.scrollLeft = keepScroll ? lastScroll : 0;
      this.updateArrows();
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const [prev, next] = this.querySelectorAll('.ll-cart-recs__arrow');
      if (!prev || !next) return;
      this.classList.toggle('ll-cart-recs--static', max <= 2);
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max - 2;
    }

    onClick(event) {
      const arrow = event.target.closest('.ll-cart-recs__arrow');
      if (arrow) {
        const card = this.track.querySelector('.ll-cart-rec');
        const step = card ? card.getBoundingClientRect().width + 12 : 160;
        this.track.scrollBy({ left: step * Number(arrow.dataset.direction), behavior: 'smooth' });
        return;
      }

      const opener = event.target.closest('[data-open-picker]');
      if (opener) {
        const picker = opener.parentElement.querySelector('.ll-cart-rec__picker');
        this.closePickers(picker);
        const open = picker.hidden;
        picker.hidden = !open;
        opener.setAttribute('aria-expanded', String(open));
        if (open) picker.querySelector('.ll-cart-rec__option:not([disabled])')?.focus();
        return;
      }

      if (event.target.closest('[data-close-picker]')) {
        this.closePickers();
        return;
      }

      const adder = event.target.closest('[data-variant-id]');
      if (adder && !adder.disabled) this.add(adder);
    }

    // Escape closes an open variant picker instead of the whole drawer.
    onKeyUp(event) {
      if (event.code !== 'Escape') return;
      const open = this.querySelector('.ll-cart-rec__picker:not([hidden])');
      if (!open) return;
      event.stopPropagation();
      this.closePickers();
      open.parentElement.querySelector('[data-open-picker]')?.focus();
    }

    closePickers(except) {
      this.querySelectorAll('.ll-cart-rec__picker').forEach((picker) => {
        if (picker === except) return;
        picker.hidden = true;
        picker.parentElement.querySelector('[data-open-picker]')?.setAttribute('aria-expanded', 'false');
      });
    }

    // Same Standard Events as Dawn's product form, so analytics see the add.
    createLinesAddEvent(variantId) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    dispatchCartErrorEvent(message, code) {
      const { CartErrorEvent } = window.StandardEvents || {};
      if (!CartErrorEvent) return;
      this.dispatchEvent(new CartErrorEvent({ error: message, code }));
    }

    resolveLinesAdd(deferred) {
      if (!deferred) return;
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      const pending =
        typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());
      pending
        .then((cart) => {
          if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        })
        .catch((e) => deferred.reject(e));
    }

    async add(button) {
      const card = button.closest('.ll-cart-rec');
      const error = card.querySelector('.ll-cart-rec__error');
      if (card.classList.contains('is-loading')) return;
      card.classList.add('is-loading');
      error.hidden = true;

      const variantId = button.dataset.variantId;
      const deferred = this.createLinesAddEvent(variantId);

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (data.status) {
          error.textContent = data.description || data.message || window.cartStrings.error;
          error.hidden = false;
          this.dispatchCartErrorEvent(error.textContent, 'INVALID');
          deferred?.reject(new Error(error.textContent));
          return;
        }
        this.resolveLinesAdd(deferred);
        // Let the /cart page and other cart listeners refresh, as Dawn's product form does.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'll-cart-recs', productVariantId: variantId, cartData: data });
        const drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections) {
          drawer.renderContents({ id: data.items?.[0]?.product_id, sections: data.sections });
          // The drawer is already open, so renderContents doesn't re-trap focus on the new markup.
          setTimeout(() => trapFocus(document.getElementById('CartDrawer'), drawer.querySelector('.drawer__inner')));
        } else {
          window.location.href = window.routes.cart_url;
        }
      } catch (e) {
        error.textContent = window.cartStrings.error;
        error.hidden = false;
        this.dispatchCartErrorEvent(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
        deferred?.reject(e);
      } finally {
        card.classList.remove('is-loading');
      }
    }
  }

  customElements.define('ll-cart-recs', LlCartRecs);
})();
